import { useMutation } from "@tanstack/react-query"
import { useLocation } from "@tanstack/react-router"
import { Loader2 } from "lucide-react"
import { useMemo } from "react"
import { toast } from "sonner"

import { updateSku } from "@/api/jobs/campaign"
import { GoBack } from "@/components/shared/layout"
import { Button } from "@/components/ui"
import type { ProductCampaignItem } from "@/features/jobs/explore/components/campaign/actions/sku-selection"
import { SkuSelection } from "@/features/jobs/explore/components/campaign/actions/sku-selection"
import { useQueryCampaignById } from "@/modules/helpers/hooks"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { useMultipleSkuSelection } from "@/modules/helpers/hooks/use-multiple-sku-selection"
import { useSkuSelection } from "@/modules/helpers/hooks/use-sku-selection"
import { extractSaleProps } from "@/modules/helpers/utils/campaign-form"

interface CampaignUpdateSkuPageProps {
  id: string
}

const CampaignUpdateSkuPage = ({ id }: CampaignUpdateSkuPageProps) => {
  const location = useLocation()

  const { data, isLoading } = useQueryCampaignById(id)

  const { mutateAsync: updateSkuMutation, isPending: isUpdatingSku } = useMutation({
    mutationFn: ({ orderSampleId, skuId }: { orderSampleId: string; skuId: string }) =>
      updateSku({ orderSampleId, skuId })
  })

  const hasProductCampaigns = !!data?.productCampaigns?.length
  const searchParams = new URLSearchParams(location.search)
  const requestProductSamplesIdParam = searchParams.get("requestProductSamplesId")

  // Multiple-SKU mode (campaign có nhiều sản phẩm con)
  const productCampaignSaleProps: ProductCampaignItem[] = useMemo(() => {
    if (!hasProductCampaigns || !data?.productCampaigns) return []
    return data.productCampaigns.map((item) => ({
      saleProps: Array.isArray(item?.saleProps)
        ? item.saleProps
        : extractSaleProps(item?.skus || []),
      title: item?.title,
      thumbnail: item?.images?.[0] || "",
      id: item?.id?.toString()
    }))
  }, [hasProductCampaigns, data?.productCampaigns])

  const { selectedSkus, togglePropValue } = useMultipleSkuSelection()

  // Single-SKU mode (campaign chỉ có 1 products)
  const singleSaleProps = useMemo(() => {
    if (hasProductCampaigns || !data?.products) return []
    return Array.isArray(data.products.saleProps)
      ? data.products.saleProps
      : extractSaleProps(data.products.skus || [])
  }, [hasProductCampaigns, data?.products])

  const { saleProp: selectedSaleProp, togglePropId } = useSkuSelection({
    products: data?.products
  })

  // Helper: check if sku matches all selected propValueIds
  const doesSkuMatchSelections = (
    sku: { skuSaleProps?: Array<{ propId?: string | number; propValueId?: string | number }> },
    selections: Record<string, string>
  ): boolean => {
    // If no selections, no match
    if (!selections || Object.keys(selections).length === 0) return false

    const saleProps = sku?.skuSaleProps || []
    return Object.entries(selections).every(([propId, propValueId]) =>
      saleProps.some(
        (p) =>
          p?.propId?.toString() === propId?.toString() &&
          p?.propValueId?.toString() === propValueId?.toString()
      )
    )
  }

  // Resolve selected SKU IDs from selections
  const selectedSkuIds = useMemo(() => {
    if (hasProductCampaigns) {
      if (
        !data?.productCampaigns?.length ||
        !selectedSkus ||
        Object.keys(selectedSkus).length === 0
      )
        return []
      const result: string[] = []
      data.productCampaigns.forEach((productCampaign) => {
        const productId = productCampaign?.id?.toString() || ""
        const selections = selectedSkus[productId]
        if (!selections || Object.keys(selections).length === 0) return
        const matched = productCampaign?.skus?.find((sku) =>
          doesSkuMatchSelections(sku, selections)
        )
        if (matched?.id) {
          result.push(matched.id.toString())
        }
      })
      return result
    } else {
      if (!data?.products?.skus || !selectedSaleProp || Object.keys(selectedSaleProp).length === 0)
        return []
      const matched = data.products.skus.find((sku) =>
        doesSkuMatchSelections(sku, selectedSaleProp)
      )
      return matched?.id ? [matched.id.toString()] : []
    }
  }, [
    hasProductCampaigns,
    data?.productCampaigns,
    data?.products?.skus,
    selectedSkus,
    selectedSaleProp
  ])

  const handleCancel = () => {
    window.history.back()
  }

  const handleSave = () => {
    if (isUpdatingSku) return

    // Basic validation
    if (!requestProductSamplesIdParam) {
      toast.error("Thiếu Order sample id")
      return
    }
    if (!selectedSkuIds.length) {
      toast.error("Vui lòng chọn SKU")
      return
    }

    const updateSkus = async () => {
      try {
        if (hasProductCampaigns) {
          await Promise.all(
            selectedSkuIds.map((skuId) =>
              updateSkuMutation({ orderSampleId: requestProductSamplesIdParam, skuId })
            )
          )
        } else {
          await updateSkuMutation({
            orderSampleId: requestProductSamplesIdParam,
            skuId: selectedSkuIds[0]
          })
        }
        toast.success("Cập nhật SKU thành công")
        setTimeout(() => {
          window.history.back()
        }, 1000)
      } catch {
        toast.error("Cập nhật SKU thất bại, vui lòng chọn mã sản phẩm khác")
      }
    }

    void updateSkus()
  }

  return (
    <div className="flex flex-col h-screen bg-white">
      <div className="bg-white sticky top-0 z-20">
        <GoBack />
      </div>

      <div
        className={cn("flex-1 overflow-y-auto px-4 pb-4", {
          "opacity-50 pointer-events-none": isLoading || !data?.id
        })}
      >
        <h1 className="text-base font-semibold mt-2 mb-3">Cập nhật SKU</h1>
        <SkuSelection
          productCampaignSaleProps={hasProductCampaigns ? productCampaignSaleProps : undefined}
          selectedSkus={hasProductCampaigns ? selectedSkus : undefined}
          onSelectMultiple={hasProductCampaigns ? togglePropValue : undefined}
          saleProps={!hasProductCampaigns ? singleSaleProps : undefined}
          selectedSaleProp={!hasProductCampaigns ? selectedSaleProp : undefined}
          onSelect={!hasProductCampaigns ? togglePropId : undefined}
          showTitle={false}
        />
      </div>

      <div className="border-t border-gray-200 bg-white p-3 flex gap-2 sticky bottom-0 z-20">
        <Button
          variant="outline"
          onClick={handleCancel}
          className="w-20 !h-11 rounded-xl font-medium bg-gray-100 border-primary-800 text-primary-800"
          disabled={isUpdatingSku}
        >
          Hủy
        </Button>
        <Button
          onClick={handleSave}
          className="flex-1 !h-11 bg-primary-800 text-white rounded-xl font-medium"
          disabled={isUpdatingSku || !selectedSkuIds.length}
        >
          {isUpdatingSku && <Loader2 className="w-4 h-4 animate-spin" />}
          Lưu
        </Button>
      </div>
    </div>
  )
}

export default CampaignUpdateSkuPage
